import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

const FILTERS = Object.freeze({
  ALL: 'all',
  PUBLIC: 'public',
  PRIVATE: 'private',
  ACTIVE: 'active',
});

const normalizeVisibility = (template) => String(template?.visibility || 'public').toLowerCase();

export default function TemplateGallery({
  templates = [],
  selectedTemplateId = '',
  activeTemplateId = '',
  onSelect,
  onPreview,
  onApply,
  onEdit,
  onCreateBlank,
  applying = false,
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(FILTERS.ALL);

  const hasPrivateTemplates = useMemo(
    () => templates.some((template) => normalizeVisibility(template) === 'private'),
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    return templates.filter((template) => {
      const visibility = normalizeVisibility(template);
      const isActive = String(template?.id || '') === String(activeTemplateId || '');
      if (filter === FILTERS.PUBLIC && visibility !== 'public') return false;
      if (filter === FILTERS.PRIVATE && visibility !== 'private') return false;
      if (filter === FILTERS.ACTIVE && !isActive) return false;
      if (!normalizedQuery) return true;
      const haystack = [template?.name, template?.description, template?.status, visibility]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [activeTemplateId, filter, query, templates]);

  return (
    <Box className="ps-template-gallery">
      <Box className="ps-template-gallery__heading">
        <Box>
          <Typography variant="h6" className="ps-template-gallery__title">
            Templates
          </Typography>
          <Typography variant="body2" className="ps-template-gallery__subtitle">
            Start with a professionally designed storefront, one of your saved templates, or a blank canvas.
          </Typography>
        </Box>
        {onCreateBlank ? (
          <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={onCreateBlank}>
            Blank canvas
          </Button>
        ) : null}
      </Box>

      <Box className="ps-template-gallery__filters">
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search templates"
          className="ps-template-gallery__search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(_event, value) => value && setFilter(value)}
          aria-label="Template filter"
          className="ps-template-gallery__toggle"
        >
          <ToggleButton value={FILTERS.ALL}>All</ToggleButton>
          <ToggleButton value={FILTERS.PUBLIC}>Starter / Public</ToggleButton>
          {hasPrivateTemplates ? <ToggleButton value={FILTERS.PRIVATE}>My Templates</ToggleButton> : null}
          {activeTemplateId ? <ToggleButton value={FILTERS.ACTIVE}>Active</ToggleButton> : null}
        </ToggleButtonGroup>
      </Box>

      <Box className="ps-template-gallery__grid">
        {filteredTemplates.map((template) => {
          const id = String(template?.id || '');
          const visibility = normalizeVisibility(template);
          const selected = id === String(selectedTemplateId || '');
          const active = id === String(activeTemplateId || '');
          const previewImage = template?.preview_url || template?.previewUrl || template?.thumbnail_url || template?.thumbnailUrl || '';

          return (
            <Box
              key={id}
              className={`ps-template-card${selected ? ' is-selected' : ''}${active ? ' is-active' : ''}`}
              onClick={() => onSelect?.(template)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onSelect?.(template);
              }}
            >
              <Box className="ps-template-card__preview">
                {previewImage ? (
                  <img src={previewImage} alt={`${template?.name || 'Template'} preview`} />
                ) : (
                  <Box className="ps-template-card__placeholder">
                    <AutoAwesomeRoundedIcon />
                    <Typography variant="caption">Storefront preview</Typography>
                  </Box>
                )}
                <Box className="ps-template-card__badges">
                  {active ? <Chip size="small" icon={<CheckCircleRoundedIcon />} label="Active" className="ps-template-card__active-chip" /> : null}
                  <Chip size="small" label={visibility === 'private' ? 'Private' : 'Public'} variant="outlined" />
                </Box>
              </Box>

              <Box className="ps-template-card__body">
                <Box className="ps-template-card__copy">
                  <Typography className="ps-template-card__name">{template?.name || id || 'Untitled template'}</Typography>
                  {template?.description ? (
                    <Typography variant="caption" className="ps-template-card__description">
                      {template.description}
                    </Typography>
                  ) : null}
                </Box>

                <Box className="ps-template-card__actions" onClick={(event) => event.stopPropagation()}>
                  {onPreview ? (
                    <Button size="small" variant="text" startIcon={<VisibilityRoundedIcon />} onClick={() => onPreview(template)}>
                      Preview
                    </Button>
                  ) : null}
                  {onEdit ? (
                    <Button size="small" variant="text" startIcon={<EditRoundedIcon />} onClick={() => onEdit(template)}>
                      Edit
                    </Button>
                  ) : null}
                  {onApply ? (
                    <Button
                      size="small"
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() => onApply(template)}
                      disabled={applying}
                    >
                      {active ? 'Re-apply' : 'Use template'}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {!filteredTemplates.length ? (
        <Box className="ps-template-gallery__empty">
          <Typography sx={{ fontWeight: 700 }}>No templates match this view.</Typography>
          <Typography variant="body2">Try another search or filter.</Typography>
        </Box>
      ) : null}
    </Box>
  );
}
